import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api.model';
import { IUser } from '../models/user.model';

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private apiUrl = `${environment.apiUrl}/user`;

  constructor(private http: HttpClient) { }

  /**
   * Get paginated users with search and sorting
   */
  getUsers(
    page: number = 1,
    pageSize: number = 10,
    search: string = '',
    sortField: string = 'employeeId',
    sortDirection: string = 'asc'
  ): Observable<ApiResponse<PaginatedResponse<IUser>>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<ApiResponse<PaginatedResponse<IUser>>>(`${this.apiUrl}`, { params });
  }

  /**
   * Get a specific user by ID
   * @param id User ID
   */
  getUserById(id: string): Observable<ApiResponse<IUser>> {
    // Note: Backend doesn't seem to have a route for getting a single user
    // This is a placeholder that would need to be implemented in the backend
    return this.http.get<ApiResponse<IUser>>(`${this.apiUrl}/${id}`);
  }
  /**
   * Check whether a user with this Mitarbeiternummer exists — errors with 404 if not.
   * Open to every logged-in role (unlike the rest of this service, which is admin-only).
   * @param employeeId Mitarbeiternummer
   */
  checkEmployeeIdExists(employeeId: string): Observable<ApiResponse<{ employeeId: number }>> {
    return this.http.get<ApiResponse<{ employeeId: number }>>(`${this.apiUrl}/employee/${encodeURIComponent(employeeId)}/exists`);
  }

  /**
   * Get firstname and lastname of the user with this Mitarbeiternummer — admin-only, 404 if unknown.
   * @param employeeId Mitarbeiternummer
   */
  getEmployeeName(employeeId: string): Observable<ApiResponse<{ employeeId: number; firstname: string; lastname: string }>> {
    return this.http.get<ApiResponse<{ employeeId: number; firstname: string; lastname: string }>>(
      `${this.apiUrl}/employee/${encodeURIComponent(employeeId)}`
    );
  }

  /**
   * Create a new user
   * @param user User data
   */
  createUser(user: IUser): Observable<ApiResponse<IUser>> {
    return this.http.post<ApiResponse<IUser>>(this.apiUrl, user);
  }

  /**
   * Update user information
   * @param id User ID
   * @param user Updated user data
   */
  updateUser(id: string, user: IUser): Observable<IUser> {
    return this.http.put<{ success: boolean; data: IUser }>(`${this.apiUrl}/${id}`, user).pipe(
      catchError(error => {
        console.error(`Error updating user ${id}:`, error);
        return throwError(() => new Error('Failed to update user'));
      }),
      map(response => response.data)
    );
  }

  /**
   * Delete a user
   * @param id User ID
   */
  deleteUser(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }
}
